import { ChangeEventHandler, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/Button';
import Checkbox from '../../../components/Checkbox/Checkbox';
import Input from '../../../components/Input/Input';
import './TaxAdvisorDatevConfiguration.scss';

type ExportFormatType = 'DATEV CSV' | 'CSV';
type GegenkontoType = 'SKR03' | 'SKR04' | 'custom';
type KontoFormatType = 5 | 6 | 'custom';

interface CountryGegenKonto {
  high: string;
  middle?: string;
  low?: string;
  IG?: string;
  AL?: string;
  steuerschlüssel: string;
}

interface KontoItem {
  5: string;
  6: string;
  custom: string;
}

export default function TaxAdvisorDatevConfiguration() {
  const { t } = useTranslation();

  const prevKontoItems = useRef<{ [key: string]: KontoItem }>();
  const prevGegenKontoItems = useRef<{ [key: string]: CountryGegenKonto }>();

  const [exportFormat, setExportFormat] =
    useState<ExportFormatType>('DATEV CSV');
  const [kontoFormat, setKontoFormat] = useState<KontoFormatType>(5);
  const [gegenKontoFormat, setGegenKontoFormat] =
    useState<GegenkontoType>('SKR03');
  const [isKontoBtnsShown, setIsKontoBtnsShown] = useState(false);
  const [isGegenKontoBtnsShown, setIsGegenKontoBtnsShown] = useState(false);

  const [kontoItems, setKontoItems] = useState<{
    [key: string]: KontoItem;
  }>(initialKonto);
  const [gegenKontoItems, setGegenKontoItems] = useState<{
    [key: string]: CountryGegenKonto;
  }>(initialGegenKontoSKR03);

  useEffect(() => {
    if (gegenKontoFormat === 'SKR03') {
      setGegenKontoItems(initialGegenKontoSKR03);
    }
    if (gegenKontoFormat === 'SKR04') {
      setGegenKontoItems(initialGegenKontoSKR04);
    }
    if (gegenKontoFormat === 'custom') {
      prevGegenKontoItems.current = gegenKontoItems;
    }
    // eslint-disable-next-line
  }, [gegenKontoFormat]);
  useEffect(() => {
    if (kontoFormat === 'custom') {
      prevKontoItems.current = kontoItems;
    }
    // eslint-disable-next-line
  }, [kontoFormat]);

  const onChangeExportFormat = (v: ExportFormatType) => () =>
    setExportFormat(v);
  const onChangeGegenKontoFormat = (v: GegenkontoType) => () => {
    if (v === 'custom') {
      setIsGegenKontoBtnsShown(true);
    }
    setGegenKontoFormat(v);
  };
  const onChangeKontoFormat = (v: KontoFormatType) => () => {
    if (v === 'custom') {
      setIsKontoBtnsShown(true);
    }
    setKontoFormat(v);
  };

  const onChangeCustomKontoFormat: (
    k: string
  ) => ChangeEventHandler<HTMLInputElement> = (k: string) => (e) => {
    if (isNaN(+e.target.value)) return;
    if (!isKontoBtnsShown) setIsKontoBtnsShown(true);

    setKontoItems((p) => ({
      ...p,
      [k]: { ...p[k], custom: e.target.value },
    }));
  };
  const onChangeGegenKontoItems: (
    k: string,
    prop: keyof CountryGegenKonto
  ) => ChangeEventHandler<HTMLInputElement> = (k, prop) => (e) => {
    if (isNaN(+e.target.value)) return;
    if (!isGegenKontoBtnsShown) setIsGegenKontoBtnsShown(true);

    setGegenKontoItems((p) => ({
      ...p,
      [k]: { ...p[k], [prop]: e.target.value },
    }));
  };

  return (
    <div className='TaxAdvisorDatevConfiguration flex-grow-1 p-5'>
      <div className='container'>
        <div className='Paper Paper--sm mx-auto pt-3 pb-4 px-4'>
          <div className='Text--30 fw-900 pb-4 border-bottom mb-2'>
            {t('DATEV configuration')}
          </div>
          <div className='pb-3 border-bottom mb-2'>
            <div className='row row-cols-auto gx-3'>
              <div
                className='col pe-3'
                style={{
                  minWidth: 122,
                }}
              >
                Export format
              </div>
              <div className='col'>
                <Checkbox
                  id='exportFormat-DATEV_CSV'
                  type='radio'
                  onChange={onChangeExportFormat('DATEV CSV')}
                  checked={exportFormat === 'DATEV CSV'}
                  className='Checkbox--ClientProfile'
                >
                  DATEV CSV
                </Checkbox>
              </div>
              <div className='col'>
                <Checkbox
                  id='exportFormat-CSV'
                  type='radio'
                  onChange={onChangeExportFormat('CSV')}
                  checked={exportFormat === 'CSV'}
                  className='Checkbox--ClientProfile'
                >
                  CSV
                </Checkbox>
              </div>
            </div>
          </div>
          <div className='pb-3 border-bottom mb-2'>
            <div className='row row-cols-auto gx-4 mb-2'>
              <div
                className='col'
                style={{
                  minWidth: 122,
                }}
              >
                <div>Konto Format</div>
              </div>
              <div className='col flex-grow-1'>
                <div className='row'>
                  <div className='col'>
                    <Checkbox
                      id='exportFormat-CSV'
                      type='radio'
                      onChange={onChangeKontoFormat(5)}
                      checked={kontoFormat === 5}
                      className='Checkbox--ClientProfile'
                    >
                      {t('*n* digits').replace('*n*', '5')}
                    </Checkbox>
                  </div>
                  <div className='col'>
                    <Checkbox
                      id='exportFormat-CSV'
                      type='radio'
                      onChange={onChangeKontoFormat(6)}
                      checked={kontoFormat === 6}
                      className='Checkbox--ClientProfile'
                    >
                      {t('*n* digits').replace('*n*', '6')}
                    </Checkbox>
                  </div>
                  <div className='col'>
                    <Checkbox
                      id='exportFormat-CSV'
                      type='radio'
                      onChange={onChangeKontoFormat('custom')}
                      checked={kontoFormat === 'custom'}
                      className='Checkbox--ClientProfile'
                    >
                      {t('Custom')}
                    </Checkbox>
                  </div>
                </div>
              </div>
            </div>
            {Object.entries(kontoItems).map(([k, v]) => (
              <div
                className='row row-cols-auto gx-4 align-items-center mb-2'
                key={k}
              >
                <div
                  className='col Text--gray'
                  style={{
                    minWidth: 122,
                  }}
                >
                  <div>{k}</div>
                </div>
                <div className='col flex-grow-1 align-items-center'>
                  <div className='row align-items-center'>
                    <div className='col Text--gray'>
                      <div className='ps-4'>{v[5]}</div>
                    </div>
                    <div className='col Text--gray'>
                      <div className='ps-4'>{v[6]}</div>
                    </div>
                    <div className='col'>
                      <div className='ps-4'>
                        <Input
                          value={kontoItems[k].custom}
                          onChange={onChangeCustomKontoFormat(k)}
                          className='Input--TaxAdvisorDatevConfiguration pb-0'
                          disabled={kontoFormat !== 'custom'}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {kontoFormat === 'custom' && isKontoBtnsShown && (
              <div className='pt-2'>
                <div className='row pt-2 row-cols-auto gx-2'>
                  <div className='col'>
                    <Button
                      onClick={() => {
                        setIsKontoBtnsShown(false);
                        prevKontoItems.current = kontoItems;
                      }}
                    >
                      {t('Save')}
                    </Button>
                  </div>
                  <div className='col'>
                    <Button
                      styling='outline'
                      onClick={() => {
                        prevKontoItems.current &&
                          setKontoItems(prevKontoItems.current);
                        setIsKontoBtnsShown(false);
                      }}
                    >
                      {t('Cancel')}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className='row row-cols-auto gx-3 mb-4'>
            <div
              className='col pe-3'
              style={{
                minWidth: 122,
              }}
            >
              Gegenkonto
            </div>
            <div className='col'>
              <Checkbox
                id='gegenKontoFormat-SKR03'
                type='radio'
                onChange={onChangeGegenKontoFormat('SKR03')}
                checked={gegenKontoFormat === 'SKR03'}
                className='Checkbox--ClientProfile'
              >
                SKR03
              </Checkbox>
            </div>
            <div className='col'>
              <Checkbox
                id='gegenKontoFormat-SKR04'
                type='radio'
                onChange={onChangeGegenKontoFormat('SKR04')}
                checked={gegenKontoFormat === 'SKR04'}
                className='Checkbox--ClientProfile'
              >
                SKR04
              </Checkbox>
            </div>
            <div className='col'>
              <Checkbox
                id='gegenKontoFormat-custom'
                type='radio'
                onChange={onChangeGegenKontoFormat('custom')}
                checked={gegenKontoFormat === 'custom'}
                className='Checkbox--ClientProfile'
              >
                {t('Custom')}
              </Checkbox>
            </div>
          </div>
          <div className='TaxAdvisorDatevConfiguration__grid d-grid mb-3'>
            {GEGENKONTO_COLUMNS.map((c) => (
              <div
                key={c}
                className={`Text--gray ${
                  c === 'Steuerschlüssel' ? 'ps-2' : ''
                }`}
              >
                {c}
              </div>
            ))}
          </div>
          {Object.entries(gegenKontoItems).map(([k, v]) => (
            <div
              className='TaxAdvisorDatevConfiguration__grid d-grid mb-3'
              key={k}
            >
              <div className='d-flex align-items-center'>
                <span className='text-truncate'>{t(k)}</span>
              </div>
              <div className='d-flex align-items-center'>
                <Input
                  value={v.high}
                  onChange={onChangeGegenKontoItems(k, 'high')}
                  className='Input--TaxAdvisorDatevConfiguration pb-0'
                  disabled={gegenKontoFormat !== 'custom'}
                />
              </div>
              <div className='d-flex align-items-center'>
                {typeof v.middle === 'string' && (
                  <Input
                    value={v.middle!}
                    onChange={onChangeGegenKontoItems(k, 'middle')}
                    className='Input--TaxAdvisorDatevConfiguration pb-0'
                    disabled={gegenKontoFormat !== 'custom'}
                  />
                )}
              </div>
              <div className='d-flex align-items-center'>
                {typeof v.low === 'string' && (
                  <Input
                    value={v.low!}
                    onChange={onChangeGegenKontoItems(k, 'low')}
                    className='Input--TaxAdvisorDatevConfiguration pb-0'
                    disabled={gegenKontoFormat !== 'custom'}
                  />
                )}
              </div>
              <div className='d-flex align-items-center'>
                {typeof v.IG === 'string' && (
                  <Input
                    value={v.IG!}
                    onChange={onChangeGegenKontoItems(k, 'IG')}
                    className='Input--TaxAdvisorDatevConfiguration pb-0'
                    disabled={gegenKontoFormat !== 'custom'}
                  />
                )}
              </div>
              <div className='d-flex align-items-center'>
                {typeof v.AL === 'string' && (
                  <Input
                    value={v.AL!}
                    onChange={onChangeGegenKontoItems(k, 'AL')}
                    className='Input--TaxAdvisorDatevConfiguration pb-0'
                    disabled={gegenKontoFormat !== 'custom'}
                  />
                )}
              </div>
              <div className='ps-2 d-flex align-items-center'>
                <Input
                  value={v.steuerschlüssel}
                  onChange={onChangeGegenKontoItems(k, 'steuerschlüssel')}
                  className='Input--TaxAdvisorDatevConfiguration pb-0'
                  disabled={gegenKontoFormat !== 'custom'}
                />
              </div>
            </div>
          ))}
          {gegenKontoFormat === 'custom' && isGegenKontoBtnsShown && (
            <div className='pt-2'>
              <div className='row pt-2 row-cols-auto gx-2'>
                <div className='col'>
                  <Button
                    onClick={() => {
                      setIsGegenKontoBtnsShown(false);
                      prevGegenKontoItems.current = gegenKontoItems;
                    }}
                  >
                    {t('Save')}
                  </Button>
                </div>
                <div className='col'>
                  <Button
                    styling='outline'
                    onClick={() => {
                      prevGegenKontoItems.current &&
                        setGegenKontoItems(prevGegenKontoItems.current);
                      setIsGegenKontoBtnsShown(false);
                    }}
                  >
                    {t('Cancel')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const GEGENKONTO_COLUMNS = [
  'Country',
  'High',
  'Middle',
  'Low',
  'IG',
  'AL',
  'Steuerschlüssel',
];

const initialGegenKontoSKR03 = {
  DE: {
    high: '84000',
    middle: '82000',
    low: '83000',
    IG: '81250',
    AL: '81200',
    steuerschlüssel: '240',
  },
  FR: {
    high: '84005',
    middle: '82005',
    low: '83005',
    IG: '81255',
    AL: '81205',
    steuerschlüssel: '240',
  },
  IT: {
    high: '84003',
    middle: '82003',
    low: '83003',
    IG: '81253',
    AL: '81203',
    steuerschlüssel: '240',
  },
  NL: {
    high: '84008',
    middle: '82008',
    low: '83008',
    IG: '81258',
    AL: '81208',
    steuerschlüssel: '240',
  },
  AT: {
    high: '84004',
    middle: '82004',
    low: '83004',
    IG: '81254',
    AL: '81204',
    steuerschlüssel: '240',
  },
  PL: {
    high: '84001',
    middle: '82001',
    low: '83001',
    IG: '81251',
    AL: '81201',
    steuerschlüssel: '240',
  },
  ES: {
    high: '84006',
    middle: '82006',
    low: '83006',
    IG: '81256',
    AL: '81206',
    steuerschlüssel: '240',
  },
  CZ: {
    high: '84002',
    middle: '82006',
    low: '83002',
    IG: '81252',
    AL: '81202',
    steuerschlüssel: '240',
  },
  GB: {
    high: '84007',
    middle: '82002',
    low: '83007',
    IG: '81257',
    AL: '81207',
    steuerschlüssel: '240',
  },
  'OSS Netto': {
    high: '83200',
    steuerschlüssel: '240',
  },
  'OSS VAT': {
    high: '17033',
    steuerschlüssel: '240',
  },
};
const initialGegenKontoSKR04 = {
  DE: {
    high: '840000',
    middle: '820000',
    low: '830000',
    IG: '812500',
    AL: '812000',
    steuerschlüssel: '2400',
  },
  FR: {
    high: '840050',
    middle: '820050',
    low: '830050',
    IG: '812550',
    AL: '812050',
    steuerschlüssel: '2400',
  },
  IT: {
    high: '840030',
    middle: '820030',
    low: '830030',
    IG: '812530',
    AL: '812030',
    steuerschlüssel: '2400',
  },
  NL: {
    high: '840080',
    middle: '820080',
    low: '830080',
    IG: '812580',
    AL: '812080',
    steuerschlüssel: '2400',
  },
  AT: {
    high: '840040',
    middle: '820040',
    low: '830040',
    IG: '812540',
    AL: '812040',
    steuerschlüssel: '2400',
  },
  PL: {
    high: '840010',
    middle: '820010',
    low: '830010',
    IG: '812510',
    AL: '812010',
    steuerschlüssel: '2400',
  },
  ES: {
    high: '840060',
    middle: '820060',
    low: '830060',
    IG: '812560',
    AL: '812060',
    steuerschlüssel: '2400',
  },
  CZ: {
    high: '840020',
    middle: '820060',
    low: '830020',
    IG: '812520',
    AL: '812020',
    steuerschlüssel: '2400',
  },
  GB: {
    high: '840070',
    middle: '820020',
    low: '830070',
    IG: '812570',
    AL: '812070',
    steuerschlüssel: '2400',
  },
  'OSS Netto': {
    high: '832000',
    steuerschlüssel: '2400',
  },
  'OSS VAT': {
    high: '170330',
    steuerschlüssel: '2400',
  },
};

const initialKonto = {
  Amazon: { 5: '10000', 6: '100000', custom: '' },
  Ebay: { 5: '100090', 6: '1000900', custom: '' },
  Zalando: { 5: '100180', 6: '1001800', custom: '' },
  CDiscount: { 5: '100170', 6: '1001700', custom: '' },
  Undefined: { 5: '100160', 6: '1001600', custom: '' },
};
